import { useCallback } from 'react';

import { trackEvent } from '@lib/analytics/mixpanel';
import { SHARE_LINK_CREATED } from '@lib/analytics/share.constants';
import { isPickTwoCartItem, type CartLineItem } from '@lib/entities/Cart';
import useAuth from '@lib/hooks/useAuth';
import useFeature from '@lib/hooks/useFeature';
import {
    type ShareItemOutcome,
    type UseShareItemOptions,
    type UseShareItemResult
} from '@lib/hooks/useShareItem.types';
import { buildShareUrl, isShareableItem } from '@lib/utils/shareItem';

/** Web has no OS share sheet in this flow — the link goes to the clipboard. */
export const SHARE_ACTION_LABEL = 'Copy Link';

/**
 * Web half of `useShareItem` — see `useShareItem.ts` for the rationale.
 *
 * Deliberately does NOT toast: the menu-editor button shows `Link copied!` while the
 * order-detail sheet flips its own row to `Copied`, so the success affordance belongs
 * to the surface, not the action.
 */
const useShareItem = ({ source }: UseShareItemOptions = {}): UseShareItemResult => {
    const isFeatureEnabled = useFeature('share-salad-web', false);
    const { user } = useAuth();
    const isEnabled = isFeatureEnabled && !!user;

    const canShare = useCallback(
        (item: CartLineItem): boolean => isEnabled && isShareableItem(item),
        [isEnabled]
    );

    const shareItem = useCallback(
        async (item: CartLineItem): Promise<ShareItemOutcome> => {
            if (!user || !isEnabled || !isShareableItem(item)) {
                return 'skipped';
            }

            // `origin` keeps a dev/staging-generated link on the tier it was made on;
            // every link is attributable to its creator via utm_content (JSAD-6701).
            const url = buildShareUrl(item, {
                creatorUserId: user.id,
                origin: window.location.origin
            });

            trackEvent(SHARE_LINK_CREATED, {
                menu_item_id: item.menuItem.id,
                is_pick_two: isPickTwoCartItem(item),
                creator_user_id: user.id,
                ...(source ? { share_source: source } : {})
            });

            await navigator.clipboard.writeText(url);

            return 'copied';
        },
        [isEnabled, source, user]
    );

    return { isEnabled, canShare, shareItem, actionLabel: SHARE_ACTION_LABEL };
};

export default useShareItem;
